// src/App.js

import React, { useEffect } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useParams } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import { onMessage } from "firebase/messaging";
import { messaging } from "./components/firebase"; // I'm assuming this path is correct for you

// ✅ STEP 1: Import the context provider and hook, and the modal components
import { AppProvider, useAppContext } from "./context/AppContext";
import LoginModal from "./pages/LoginModal";

import SplashScreen from "./pages/SplashScreen";
import Home from "./pages/Homepage/Home";
import Dashboard from "./pages/Dashboard";
import CreateEvents from "./pages/CreateEvents";
import MyAppointments from "./pages/MyAppointments";
import ProtectedRoute from "./components/ProtectedRoute";
import MyOrgansiation from "./pages/MyOrganisation";
import ResourceDetails from "./pages/ResourceDetails";
import ServiceDetailsPage from "./pages/ServiceDetailsPage";
import OrganisationDetails from "./pages/Homepage/OrganisationDetails";
import AppointmentsDetails from "./pages/Homepage/AppointmentsDetails";
import EventsDetails from "./pages/Homepage/EventsDetails";
import AboutUs from "./pages/Homepage/AboutUs";
import Careers from "./pages/Homepage/Careers";
import News from "./pages/Homepage/News";
import PartnerWithUs from "./pages/Homepage/PartnerWithUs";
import TermsAndConditions from "./pages/Homepage/TermsAndConditions";
import PrivacyPolicy from "./pages/Homepage/PrivacyPolicy";
import RefundPolicy from "./pages/Homepage/RefundPolicy";
import EventDetails from "./pages/EventDetails";
import CalendarTimeline from "./pages/CalendarTimeline";

const ParamRedirect = ({ to, paramKey }) => {
  const params = useParams();
  const value = params[paramKey];
  return <Navigate to={`${to}/${value}`} replace />;
};

const AppContent = () => {
  const {
    showLoginModal,
    setShowLoginModal,
    location,
  } = useAppContext();

  useEffect(() => {
    // Your notification listener can stay here
    onMessage(messaging, (payload) => {
      console.log("📩 Foreground FCM message:", payload);
      toast(`${payload.notification?.title}: ${payload.notification?.body}`);
    });
  }, []);

  return (
    <>
      <Toaster position="top-center" reverseOrder={false} />
      <Routes>
        <Route path="/" element={<SplashScreen />} />
        <Route path="/home" element={<Home />} />
        {/* ✅ ADD THE NEW PUBLIC FEATURE ROUTES HERE */}
        <Route path="/organizations" element={<OrganisationDetails />} />
        <Route path="/appointments" element={<AppointmentsDetails />} />
        <Route path="/events" element={<EventsDetails />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/news" element={<News />} />{" "}
        <Route path="/partners" element={<PartnerWithUs />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/refunds" element={<RefundPolicy />} />
        {/* Your Protected Routes */}
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/events/new"
          element={
            <ProtectedRoute>
              <CreateEvents />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/appointments"
          element={
            <ProtectedRoute>
              <MyAppointments />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/organization"
          element={
            <ProtectedRoute>
              <MyOrgansiation />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/resources/:resourceId"
          element={
            <ProtectedRoute>
              <ResourceDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/services/:serviceId?"
          element={
            <ProtectedRoute>
              <ServiceDetailsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/events/:eventId"
          element={
            <ProtectedRoute>
              <EventDetails />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/calendar"
          element={
            <ProtectedRoute>
              <CalendarTimeline />
            </ProtectedRoute>
          }
        />
        {/* Legacy route redirects for cleaner URLs */}
        <Route path="/organisation-details" element={<Navigate to="/organizations" replace />} />
        <Route path="/appointments-details" element={<Navigate to="/appointments" replace />} />
        <Route path="/events-details" element={<Navigate to="/events" replace />} />
        <Route path="/about-us" element={<Navigate to="/about" replace />} />
        <Route path="/terms-and-conditions" element={<Navigate to="/terms" replace />} />
        <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
        <Route path="/refund-policy" element={<Navigate to="/refunds" replace />} />
        <Route path="/dashboard" element={<Navigate to="/app" replace />} />
        <Route path="/create-events" element={<Navigate to="/app/events/new" replace />} />
        <Route path="/my-appointments" element={<Navigate to="/app/appointments" replace />} />
        <Route path="/my-organisation" element={<Navigate to="/app/organization" replace />} />
        <Route path="/resource/:resourceId" element={<ParamRedirect to="/app/resources" paramKey="resourceId" />} />
        <Route path="/service-details" element={<Navigate to="/app/services" replace />} />
        <Route path="/event-details/:eventId" element={<ParamRedirect to="/app/events" paramKey="eventId" />} />
        <Route path="/calendar" element={<Navigate to="/app/calendar" replace />} />
      </Routes>
      {showLoginModal && (
        <LoginModal
          setShowModal={setShowLoginModal}
          city={location?.city || "Unknown"}
          pincode={location?.pincode || "Unknown"}
        />
      )}
    </>
  );
};

// This is the main App component
function App() {
  return (
    // ✅ STEP 4: Wrap your ENTIRE application with the AppProvider
    <AppProvider>
      <Router>
        <AppContent />
      </Router>
    </AppProvider>
  );
}

export default App;
